import React, {useCallback, useEffect, useRef, useState} from 'react';
import {useLocation} from 'react-router-dom';
import DeliveryLayout, { useDeliveryLayout } from '../layouts/DeliveryLayout';
import SearchBar from '../delivering/components/SearchBar';
import DeliveryTabs, {DeliveryTab} from '../delivering/components/DeliveryTabs';
import OrderCardTemporary, {DeliveringOrder} from './OrderCardTemporary';
import UploadNotification from "../delivering/components/UploadNotification";
import {useFailedUploadRetry} from "../delivering/hooks/useFailedUploadRetry";
import EmptyState from "../../../components/EmptyState";
import {
    preload,
    useExceptionDictionaries, useRetryBlockControl
} from "../store/deliveryStore";
import {BarCodeScanner} from "../../../components/BarCodeScanner";
import {Skeleton, Toast} from "antd-mobile";
import {TaskPath} from "../../../enums/DeliveryNavigatePathEnum";
import useHistoryControl from "../../../hooks/useHistoryControl";
import { useDeliveryTaskOrderList } from '../store/deliveryStore';
import {useNetwork} from "../../../hooks/useNetwork";
import PullToRefreshWrapper from "../../../components/PullToRefreshWrapper";
import {t} from "../../../i18n";
import {useQuery} from "@apollo/client";
import {CHECK_SAME_AREA_ENABLED, CheckSameAreaEnabledResponse} from "../graphql/queries/checkSameAreaEnabled";
import { sortDeliveryOrders } from '../../../utils/stopNumberUtils';

type DeliveringOrderWithStandardAddress = DeliveringOrder & { standardizedAddress?: string };

const Body: React.FC = () => {
    const location = useLocation();
    const [activeTab, setActiveTab] = useState<string>(location.state?.activeTab || 'delivering');
    const [orders, setOrders] = useState<DeliveringOrderWithStandardAddress[]>([]);
    const [searchValue, setSearchValue] = useState<string>(new URLSearchParams(location.search).get('trackingNumber') || '');
    const [uploadStatus, setUploadStatus] = useState<'retrying' | 'success' | 'none'>('none');
    const [deliveringCount, setDeliveringCount] = useState<number>(0);
    const [undeliveredCount, setUndeliveredCount] = useState<number>(0);
    const mainContainerRef = useRef<HTMLDivElement>(null);
    const { failedUploadCount, isRetrying, handleRetry, showRetryingHint, isRetryReady } = useFailedUploadRetry();
    const { setRetryBlocked } = useRetryBlockControl();
    const exceptionDictionaries = useExceptionDictionaries();
    const { isOnline } = useNetwork();
    const [refreshing, setRefreshing] = useState(false);
    const { refreshFailedCount, podAlertHeight } = useDeliveryLayout();

    useHistoryControl(TaskPath.DELIVERY_TASK, {
        useReplace: true,
        controlNavButton: false
    });
    // 根据重试状态设置数据获取阻塞
    useEffect(() => {
        if (!isRetryReady) {
            return;
        }
        const shouldBlock = failedUploadCount > 0 || showRetryingHint;
        setRetryBlocked(shouldBlock);
    }, [failedUploadCount, isRetryReady, isRetrying, setRetryBlocked, showRetryingHint]);

    const {deliveryTaskOrderList: deliveringTasks, loading, refreshDeliveryTaskOrderList} = useDeliveryTaskOrderList();

    // 查询是否启用同区域校验
    const { data: sameAreaEnabledData } = useQuery<CheckSameAreaEnabledResponse>(
        CHECK_SAME_AREA_ENABLED,
        {
            fetchPolicy: 'network-only'
        }
    );

    const hasInitialRefreshRun = useRef(false);
    useEffect(() => {
        if (!isRetryReady || hasInitialRefreshRun.current) {
            return;
        }
        hasInitialRefreshRun.current = true;
        // 只有在重试数据初始化完成后才执行校验逻辑
        preload();
        const timer = setTimeout(() => {
            if (failedUploadCount <= 0 && !showRetryingHint){
                refreshDeliveryTaskOrderList();
            }
        }, 100);

        // 清理定时器，防止内存泄漏
        return () => clearTimeout(timer);
    }, [failedUploadCount, isRetryReady, refreshDeliveryTaskOrderList, showRetryingHint]);

    useEffect(() => {
        sessionStorage.removeItem('returningFromDetail')
        if (!deliveringTasks?.length) {
            setOrders([]);
            return;
        }
        const deliveringData = deliveringTasks.filter(item => {
        return (item.isPickedUp || item.isAbnormal || item.isDelivery) && !item.isTransferOut }) || [];
        if (!deliveringData?.length){
            setOrders([]);
            return;
        }
    
        const actions = exceptionDictionaries;
        // @ts-ignore
        // @ts-ignore
        const formattedData = deliveringData.map(item => {
            const exceptionReasonCode = item.orderWithAddress?.order?.exceptionReasonCode;
            const exceptionReason = actions.find(
                action => action.exceptionReasonCode === exceptionReasonCode
            );

            const rawAddress = [
                item.orderWithAddress?.deliveryAddress?.addressLine1 || '',
                item.orderWithAddress?.deliveryAddress?.addressLine2 || '',
                item.orderWithAddress?.deliveryAddress?.city || '',
                item.orderWithAddress?.deliveryAddress?.state || '',
                item.orderWithAddress?.deliveryAddress?.zipcode || '',
                'US'
            ].filter(Boolean).join(' ');
            const standardizedAddress = item.orderWithAddress?.deliveryAddress?.standardizedAddressString || '';

            return {
                id: item.id,
                trackingNumber: item.trackingNumber || '',
                deliveryAttempts: item.orderWithAddress?.order?.deliveryAttempts || 0,
                stopNumber: item.stopNumber,
                subStopNumber: item.subStopNumber,
                fullName: item.orderWithAddress?.deliveryAddress?.fullName || '',
                address: rawAddress,
                standardizedAddress: standardizedAddress || undefined,
                routeNumber: item.routeName,
                deliveredAt: item.orderWithAddress?.order?.deliveredAt || '',
                expectedDeliveryTime: item.orderWithAddress?.order?.expectedDeliveryTime || '',
                deliveryType: item?.orderWithAddress?.order?.overallStatus || '',
                phone: item.orderWithAddress?.deliveryAddress?.phoneNumber || '',
                isPickUp:item?.isPickedUp,
                isAbnormal: item.isAbnormal,
                isDelivered: item?.orderWithAddress?.order?.isDelivered || false,
                exceptionReason: exceptionReason,
                isReturnToWhsScanned: item?.orderWithAddress?.order?.isReturnToWhsScanned,
                isDelivery:item?.isDelivery,
                isFinished: item?.orderWithAddress?.order?.isFinished,
                deliveryTaskId: item.deliveryTaskId,
                createdAt: item.deliveryTaskType?.createdAt || '',
                routeHistoryId: item.deliveryTaskType?.routeHistoryId || 0,
                routeName: item.routeName,
                agingLevel: item.agingLevel || 0,
                taskDate: item.taskDate,
                addressGroupKey: item.addressGroupKey
            };
        });
        setOrders(formattedData);
    }, [deliveringTasks, activeTab,sameAreaEnabledData?.checkSameAreaEnabled]);

    useEffect(() => {
        const newDeliveringCount = orders.filter(order =>
            order.isPickUp && !order.isAbnormal && order.isDelivery && !order.isDelivered && !order.isFinished && order.deliveryType !== 'PRE_DELIVERED'
        ).length;

        const newUndeliveredCount = orders.filter(order =>
            order.isAbnormal && !order.isReturnToWhsScanned && order.isDelivery && !order.isDelivered && !order.isFinished && order.deliveryType !== 'PRE_DELIVERED'
        ).length;

        setDeliveringCount(newDeliveringCount);
        setUndeliveredCount(newUndeliveredCount);
    }, [orders]);

    useEffect(() => {
        if (showRetryingHint) {
            setUploadStatus('retrying');
        } else if (uploadStatus === 'retrying') {
            setUploadStatus('success');
            refreshFailedCount();
        }
    }, [showRetryingHint, uploadStatus, refreshFailedCount]);

    const triggerRetry = useCallback(() => {
        if (failedUploadCount > 0 && !isRetrying) {
            handleRetry();
        }
    }, [failedUploadCount, isRetrying, handleRetry]);

    const handleNotificationClick = useCallback(() => {
        if (uploadStatus === 'success') {
            setUploadStatus('none');
        } else if (uploadStatus === 'none' && failedUploadCount > 0) {
            triggerRetry();
        }
    }, [uploadStatus, failedUploadCount, triggerRetry]);

    const filteredOrders = orders.filter(order =>
        (activeTab === 'delivering' && order.isPickUp && !order.isAbnormal && order.isDelivery && !order.isDelivered && !order.isFinished && order.deliveryType !== 'PRE_DELIVERED')
        || (activeTab === 'undelivered' && order.isAbnormal && !order.isReturnToWhsScanned && order.isDelivery && !order.isDelivered && !order.isFinished && order.deliveryType !== 'PRE_DELIVERED')
    ).filter(order =>
        order.trackingNumber.toLowerCase().includes(searchValue.toLowerCase())
    );


    // 根据 taskDate 和 addressGroupKey 分组订单
    const groupOrdersByTaskDateAndAddress = (orders: DeliveringOrderWithStandardAddress[]) => {
        const groups: { [key: string]: DeliveringOrderWithStandardAddress[] } = {};

        orders.forEach(order => {
            // 使用 taskDate 和 addressGroupKey 作为分组键
            // 只有相同 taskDate 和 addressGroupKey 的订单才会聚合在一起
            const groupKey = `${order.taskDate || ''}|${order.addressGroupKey || ''}`;
            if (!groups[groupKey]) {
                groups[groupKey] = [];
            }
            groups[groupKey].push(order);
        });

        return groups;
    };

    const groupedOrders = groupOrdersByTaskDateAndAddress(filteredOrders);

    const tabs: DeliveryTab[] = [
        {
            key: 'delivering',
            title: `${t('pages.delivery.delivering.deliveringTab')}`,
            count: deliveringCount
        },
        {
            key: 'undelivered',
            title: `${t('pages.delivery.delivering.unDeliveredTab')}`,
            count: undeliveredCount
        }
    ];

    const handleTabChange = (key: string) => {
        setActiveTab(key);
    };

    const handleSearch = async (value: string) => {
        setSearchValue(value);
    };

    // 恢复主容器滚动位置
    useEffect(() => {
        if (!loading && orders.length > 0) {
            const savedScrollTop = sessionStorage.getItem('deliveringPageScrollTop');
            if (savedScrollTop && mainContainerRef.current) {
                setTimeout(() => {
                    if (mainContainerRef.current) {
                        mainContainerRef.current.scrollTop = parseInt(savedScrollTop);
                        sessionStorage.removeItem('deliveringPageScrollTop');
                    }
                }, 200);
            }
        }
    }, [loading, orders]);

    const handleRefresh = async () => {
        if (!isOnline) {
            Toast.show({
                content: 'Network connection unavailable',
                position: 'bottom'
            });
            return;
        }

        setRefreshing(true);
        try {
            await refreshDeliveryTaskOrderList();
            await refreshFailedCount();
        } catch (error) {
            Toast.show({
                content: 'Refresh failed, please try again later',
                position: 'bottom'
            });
        } finally {
            setRefreshing(false);
        }
    };

    return (
        <PullToRefreshWrapper onRefresh={handleRefresh}>
            <div className="flex flex-col h-screen bg-gray-50">
                {uploadStatus !== 'none' && (
                    <div className="px-2 pt-2">
                        <UploadNotification
                            status={uploadStatus}
                            onClick={handleNotificationClick}
                        />
                    </div>
                )}

                <div
                    id="delivering-main-container"
                    className="flex flex-col flex-1 gap-2 p-2 pt-0 overflow-y-auto pb-20"
                    ref={mainContainerRef}
                    style={{ paddingBottom: `calc(80px + ${podAlertHeight}px + env(safe-area-inset-bottom))` }}
                >
                    <SearchBar onSearch={handleSearch}/>

                    <DeliveryTabs
                        tabs={tabs}
                        activeKey={activeTab}
                        onChange={handleTabChange}
                    />

                    <div className="flex-1">
                        {loading && !refreshing ? (
                            <div className="flex flex-col h-screen">
                                <Skeleton.Title animated />
                                <Skeleton.Paragraph lineCount={3} animated />
                                <Skeleton.Title animated />
                                <Skeleton.Paragraph lineCount={3} animated />
                                <Skeleton.Title animated />
                                <Skeleton.Paragraph lineCount={3} animated />
                                <Skeleton.Title animated />
                                <Skeleton.Paragraph lineCount={3} animated />
                                <Skeleton.Title animated />
                                <Skeleton.Paragraph lineCount={3} animated />
                            </div>
                        ):(
                            <>
                                {filteredOrders.length > 0 ? (
                                    <div>
                                        {Object.entries(groupedOrders).map(([groupKey, ordersInGroup]) => {
                                            const groupOrderIdList = ordersInGroup.map(o => o.id);
                                            if (ordersInGroup.length === 1) {
                                                return (
                                                    <OrderCardTemporary
                                                        key={ordersInGroup[0].id}
                                                        order={ordersInGroup[0]}
                                                        activeTab={activeTab}
                                                        groupOrderIdList={groupOrderIdList}
                                                    />
                                                );
                                            }

                                            return (
                                                <div
                                                    key={groupKey}
                                                    className="border-2 border-blue-400 rounded-lg mb-3 relative bg-blue-50"
                                                >
                                                    <div className="absolute top-0 left-0 bg-blue-400 text-white text-xs px-2 py-1 rounded-br z-10 whitespace-nowrap">
                                                        {t('pages.delivery.delivering.addressGroup', {orderCount: ordersInGroup.length})}
                                                    </div>
                                                    {ordersInGroup.length > 0 && (
                                                        <div className="pt-8 px-2 pb-2">
                                                            <p className="text-base font-semibold break-words min-w-0">
                                                                {ordersInGroup[0].standardizedAddress || ordersInGroup[0].address}
                                                            </p>
                                                        </div>
                                                    )}
                                                    {ordersInGroup.map((order) => (
                                                        <OrderCardTemporary
                                                            key={order.id}
                                                            order={order}
                                                            activeTab={activeTab}
                                                            hideAddress={true}
                                                            isGrouped={true}
                                                            groupOrderIdList={groupOrderIdList}
                                                        />
                                                    ))}
                                                </div>
                                            );
                                        })}
                                    </div>
                                ) : (
                                    <EmptyState/>
                                )}
                            </>
                        )}
                    </div>
                </div>
            </div>
        </PullToRefreshWrapper>
    );
};


const DeliveringPage: React.FC = () => {
    const { isOnline } = useNetwork();
    
    useEffect(() => {
        console.log('DeliveringTemporaryPage');
    }, []);

    return (
        <DeliveryLayout title={`${t('pages.delivery.delivering.title')}`}>
            {isOnline && <BarCodeScanner showManualInputIcon={false}/>}
            <Body/>
        </DeliveryLayout>
    );
};

export default DeliveringPage;